import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../lib/api';
import type { Organization } from '../types';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Building2,
  Users,
  Calendar,
  MoreVertical,
  X,
  UserPlus,
  UserMinus,
  Settings,
  BarChart3,
  Mail,
  Palette,
  CreditCard,
  Upload,
  Shield,
} from 'lucide-react';

interface OrganizationModalProps {
  organization?: Organization;
  isOpen: boolean;
  onClose: () => void;
  onSave: (orgData: Partial<Organization>) => void;
}

const OrganizationModal: React.FC<OrganizationModalProps> = ({ 
  organization, 
  isOpen, 
  onClose, 
  onSave 
}) => {
  const [formData, setFormData] = useState({
    name: organization?.name || '',
    description: organization?.description || '',
    domain: organization?.domain || '',
    status: organization?.status || 'active',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            {organization ? 'Edit Organization' : 'Create Organization'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domain
            </label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              placeholder="example.com"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {organization ? 'Update' : 'Create'} Organization
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface MembersModalProps {
  organization: Organization;
  isOpen: boolean;
  onClose: () => void;
}

const MembersModal: React.FC<MembersModalProps> = ({ organization, isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['organization-members', organization.id],
    queryFn: () => adminAPI.getOrganizationMembers(organization.id),
    enabled: isOpen,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users', searchTerm],
    queryFn: () => adminAPI.getUsers({ search: searchTerm, limit: 50 }),
    enabled: isOpen && searchTerm.length > 2,
  });

  const addMemberMutation = useMutation({
    mutationFn: (userId: string) => adminAPI.addOrganizationMember(organization.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members', organization.id] });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => adminAPI.removeOrganizationMember(organization.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members', organization.id] });
    },
  });

  const members = membersData || [];
  const availableUsers = usersData?.data?.filter(
    user => !members.some((member: any) => member.id === user.id)
  ) || [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            Manage Members - {organization.name}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-96">
          {/* Current Members */}
          <div className="border-r border-gray-200 pr-6">
            <h3 className="text-lg font-medium mb-4">Current Members ({members.length})</h3>
            <div className="space-y-2 overflow-y-auto max-h-80">
              {membersLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : (
                members.map((member: any) => (
                  <div key={member.id} className="flex items-center justify-between p-2 border rounded-md">
                    <div>
                      <div className="font-medium text-sm">
                        {member.firstName} {member.lastName}
                      </div>
                      <div className="text-xs text-gray-500">{member.email}</div>
                    </div>
                    <button
                      onClick={() => removeMemberMutation.mutate(member.id)}
                      disabled={removeMemberMutation.isLoading}
                      className="text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      <UserMinus className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Add Members */}
          <div className="pl-6">
            <h3 className="text-lg font-medium mb-4">Add Members</h3>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="space-y-2 overflow-y-auto max-h-64">
              {availableUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-2 border rounded-md">
                  <div>
                    <div className="font-medium text-sm">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </div>
                  <button
                    onClick={() => addMemberMutation.mutate(user.id)}
                    disabled={addMemberMutation.isLoading}
                    className="text-green-600 hover:text-green-800 disabled:opacity-50"
                  >
                    <UserPlus className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {searchTerm.length > 2 && availableUsers.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No available users found
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

interface OrganizationDetailModalProps {
  organization: Organization;
  isOpen: boolean;
  onClose: () => void;
}

const OrganizationDetailModal: React.FC<OrganizationDetailModalProps> = ({ 
  organization, 
  isOpen, 
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'members' | 'settings' | 'branding' | 'billing' | 'sso'>('overview');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  // Editable copy of the org's settings (Settings tab). Seeded from the org and
  // re-synced when a different org is opened.
  const [orgSettings, setOrgSettings] = useState<any>(organization.settings || {});
  const [settingsSaved, setSettingsSaved] = useState(false);
  React.useEffect(() => {
    setOrgSettings(organization.settings || {});
  }, [organization.id, organization.settings]);
  const queryClient = useQueryClient();

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['organization-members', organization.id],
    queryFn: () => adminAPI.getOrganizationMembers(organization.id),
    enabled: isOpen && activeTab === 'members',
  });

  const { data: usageData } = useQuery({
    queryKey: ['organization-usage', organization.id],
    queryFn: () => adminAPI.getOrganizationUsage(organization.id),
    enabled: isOpen && activeTab === 'overview',
  });

  const { data: billingData } = useQuery({
    queryKey: ['organization-billing', organization.id],
    queryFn: () => adminAPI.getOrganizationBilling(organization.id),
    enabled: isOpen && activeTab === 'billing',
  });

  const { data: ssoData } = useQuery({
    queryKey: ['organization-sso', organization.id],
    queryFn: () => adminAPI.getOrgSso(organization.id),
    enabled: isOpen && activeTab === 'sso',
  });

  const inviteMutation = useMutation({
    mutationFn: ({ email, role }: { email: string; role: string }) => 
      adminAPI.sendOrganizationInvite(organization.id, email, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members', organization.id] });
      setInviteEmail('');
      setInviteRole('member');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => adminAPI.removeOrganizationMember(organization.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members', organization.id] });
    },
  });

  const updateOrgMutation = useMutation({
    mutationFn: (updates: Partial<Organization>) => adminAPI.updateOrganization(organization.id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['organization-usage', organization.id] });
    },
  });

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (inviteEmail.trim()) {
      inviteMutation.mutate({ email: inviteEmail.trim(), role: inviteRole });
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    try {
      // Presigned S3 upload via the backend; the handler also persists logo_url
      // on the org, so just refetch to pick up the new URL.
      await adminAPI.uploadOrgLogo(organization.id, file);
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    } catch (error) {
      console.error('Logo upload failed:', error);
      alert('Failed to upload logo');
    }
  };

  const handleLogoRemove = () => {
    if (confirm('Are you sure you want to remove the organization logo?')) {
      updateOrgMutation.mutate({
        settings: {
          ...organization.settings,
          logo_url: undefined
        }
      });
    }
  };

  const handleColorChange = (color: string) => {
    // Validate hex color format
    const hexColorRegex = /^#[0-9A-F]{6}$/i;
    if (hexColorRegex.test(color)) {
      updateOrgMutation.mutate({
        settings: {
          ...organization.settings,
          primary_color: color
        }
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold">{organization.name}</h2>
            <p className="text-gray-600">{organization.description || 'No description'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'members', label: 'Members', icon: Users },
              { id: 'settings', label: 'Settings', icon: Settings },
              { id: 'branding', label: 'Branding', icon: Palette },
              { id: 'billing', label: 'Billing', icon: CreditCard },
              { id: 'sso', label: 'SSO', icon: Shield },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`group inline-flex items-center py-4 px-6 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Organization Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Users className="w-8 h-8 text-blue-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-blue-600">Total Members</p>
                      <p className="text-2xl font-bold text-blue-900">{organization.memberCount || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <BarChart3 className="w-8 h-8 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-600">API Calls (30d)</p>
                      <p className="text-2xl font-bold text-green-900">
                        {usageData?.apiCalls || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <CreditCard className="w-8 h-8 text-purple-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-purple-600">Plan</p>
                      <p className="text-2xl font-bold text-purple-900 capitalize">
                        {organization.subscription?.plan || 'Free'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Usage Charts */}
              {usageData && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Usage Analytics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Storage Usage</h4>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ 
                            width: `${Math.min((usageData.storage / (organization.subscription?.limits?.storage_gb || 1)) * 100, 100)}%` 
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {(usageData.storage / 1024 / 1024 / 1024).toFixed(2)} GB / {organization.subscription?.limits?.storage_gb || 1} GB
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">User Limit</h4>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ 
                            width: `${Math.min(((organization.memberCount || 0) / (organization.subscription?.limits?.users || 1)) * 100, 100)}%` 
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {organization.memberCount || 0} / {organization.subscription?.limits?.users || 'Unlimited'} users
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'members' && (
            <div className="space-y-6">
              {/* Invite Member Form */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Invite New Member</h3>
                <form onSubmit={handleInvite} className="flex gap-4">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="owner">Owner</option>
                  </select>
                  <button
                    type="submit"
                    disabled={inviteMutation.isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Invite
                  </button>
                </form>
              </div>

              {/* Members List */}
              <div>
                <h3 className="text-lg font-medium mb-4">Current Members</h3>
                {membersLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(membersData || []).map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-3 border rounded-md">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium">
                              {(member.firstName?.[0] || member.email[0]).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="font-medium text-sm">
                              {member.firstName} {member.lastName}
                            </div>
                            <div className="text-xs text-gray-500">{member.email}</div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                            {typeof member.role === 'string' ? member.role : 'Member'}
                          </span>
                          <button
                            onClick={() => removeMemberMutation.mutate(member.id)}
                            disabled={removeMemberMutation.isLoading}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            <UserMinus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Authentication Settings</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Require Email Verification</span>
                      <input
                        type="checkbox"
                        checked={orgSettings.require_email_verification || false}
                        onChange={(e) => setOrgSettings({ ...orgSettings, require_email_verification: e.target.checked })}
                        className="toggle"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Allow Public Signup</span>
                      <input
                        type="checkbox"
                        checked={orgSettings.allow_signup || false}
                        onChange={(e) => setOrgSettings({ ...orgSettings, allow_signup: e.target.checked })}
                        className="toggle"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Require MFA</span>
                      <input
                        type="checkbox"
                        checked={orgSettings.require_mfa || false}
                        onChange={(e) => setOrgSettings({ ...orgSettings, require_mfa: e.target.checked })}
                        className="toggle"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-4">Security Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Session Timeout (seconds)
                      </label>
                      <input
                        type="number"
                        value={orgSettings.session_timeout ?? 3600}
                        onChange={(e) => setOrgSettings({ ...orgSettings, session_timeout: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Allowed Domains (one per line)
                      </label>
                      <textarea
                        value={(orgSettings.allowed_domains || []).join('\n')}
                        onChange={(e) => setOrgSettings({ ...orgSettings, allowed_domains: e.target.value.split('\n').map((d) => d.trim()).filter(Boolean) })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        rows={3}
                        placeholder="example.com"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end items-center gap-3 pt-4 border-t">
                {settingsSaved && <span className="text-sm text-green-600">Saved</span>}
                <button
                  onClick={() => {
                    updateOrgMutation.mutate(
                      { settings: { ...(organization.settings || {}), ...orgSettings } },
                      { onSuccess: () => { setSettingsSaved(true); setTimeout(() => setSettingsSaved(false), 3000); } }
                    );
                  }}
                  disabled={updateOrgMutation.isLoading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateOrgMutation.isLoading ? 'Saving…' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'branding' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Logo & Assets</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Organization Logo
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        {organization.settings?.logo_url ? (
                          <div className="relative">
                            <img 
                              src={organization.settings.logo_url} 
                              alt="Organization Logo" 
                              className="h-16 mx-auto mb-2"
                            />
                            <button
                              onClick={() => handleLogoRemove()}
                              className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        )}
                        <div className="text-sm text-gray-500 mb-2">
                          {organization.settings?.logo_url ? 'Click to change logo' : 'Click to upload or drag and drop'}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleLogoUpload(e)}
                          className="hidden"
                          id={`logo-upload-${organization.id}`}
                        />
                        <label
                          htmlFor={`logo-upload-${organization.id}`}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {organization.settings?.logo_url ? 'Change Logo' : 'Upload Logo'}
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Recommended: PNG or JPG, max 2MB, 200x200px
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-4">Color Scheme</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Primary Color
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="color"
                          value={organization.settings?.primary_color || '#3B82F6'}
                          onChange={(e) => handleColorChange(e.target.value)}
                          className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={organization.settings?.primary_color || '#3B82F6'}
                          onChange={(e) => handleColorChange(e.target.value)}
                          className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="#3B82F6"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        This color will be used for buttons and accents in your organization's login interface
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium mb-4">Login Page Preview</h3>
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <div className="text-center">
                    <div 
                      className="w-16 h-16 rounded-lg mx-auto mb-4 flex items-center justify-center"
                      style={{ 
                        backgroundColor: `${organization.settings?.primary_color || '#3B82F6'}20`,
                        color: organization.settings?.primary_color || '#3B82F6'
                      }}
                    >
                      {organization.settings?.logo_url ? (
                        <img 
                          src={organization.settings.logo_url} 
                          alt="Logo" 
                          className="w-12 h-12 object-contain"
                        />
                      ) : (
                        <Building2 className="w-8 h-8" />
                      )}
                    </div>
                    <h4 className="text-lg font-semibold">{organization.name}</h4>
                    <p className="text-sm text-gray-600 mb-4">Sign in to your account</p>
                    <div className="space-y-2 max-w-xs mx-auto">
                      <div className="h-10 bg-white border rounded-md"></div>
                      <div className="h-10 bg-white border rounded-md"></div>
                      <div 
                        className="h-10 rounded-md text-white flex items-center justify-center text-sm font-medium"
                        style={{ backgroundColor: organization.settings?.primary_color || '#3B82F6' }}
                      >
                        Sign In
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="space-y-6">
              {billingData && !billingData.configured && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                  Stripe is not connected — showing the local subscription record. Add Stripe keys in
                  Secrets Manager (<code>authaas/stripe-dev</code>) to enable live billing &amp; invoices.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Current Plan</h3>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold capitalize">
                        {organization.subscription?.plan || 'Free'}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        organization.subscription?.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {organization.subscription?.status || 'Unknown'}
                      </span>
                    </div>
                    
                    {organization.subscription && (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Users:</span>
                          <span>{organization.subscription.limits?.users || 'Unlimited'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>API Calls:</span>
                          <span>{organization.subscription.limits?.api_calls || 'Unlimited'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Storage:</span>
                          <span>{organization.subscription.limits?.storage_gb || 'Unlimited'} GB</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-4">Usage This Month</h3>
                  <div className="border border-gray-200 rounded-lg p-4">
                    {organization.subscription && (
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Users</span>
                            <span>
                              {organization.subscription.usage?.users || 0} / {organization.subscription.limits?.users || '∞'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ 
                                width: organization.subscription.limits?.users 
                                  ? `${Math.min(((organization.subscription.usage?.users || 0) / organization.subscription.limits.users) * 100, 100)}%`
                                  : '0%'
                              }}
                            ></div>
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>API Calls</span>
                            <span>
                              {organization.subscription.usage?.api_calls || 0} / {organization.subscription.limits?.api_calls || '∞'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{ 
                                width: organization.subscription.limits?.api_calls 
                                  ? `${Math.min(((organization.subscription.usage?.api_calls || 0) / organization.subscription.limits.api_calls) * 100, 100)}%`
                                  : '0%'
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center pt-4 border-t">
                <div>
                  <p className="text-sm text-gray-600">
                    Billing period: {organization.subscription?.current_period_start 
                      ? new Date(organization.subscription.current_period_start * 1000).toLocaleDateString()
                      : 'N/A'} - {organization.subscription?.current_period_end
                      ? new Date(organization.subscription.current_period_end * 1000).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Invoices</h3>
                {billingData?.invoices?.length ? (
                  <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                    {billingData.invoices.map((inv: any) => (
                      <div key={inv.id} className="flex items-center justify-between px-4 py-2 text-sm">
                        <span>{inv.created ? new Date(inv.created).toLocaleDateString() : inv.id}</span>
                        <span>${((inv.amount || 0) / 100).toFixed(2)}</span>
                        <span className="capitalize text-gray-500">{inv.status}</span>
                        {inv.url && (
                          <a href={inv.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">View</a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No invoices yet.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'sso' && (
            <SsoTab orgId={organization.id} ssoData={ssoData} />
          )}
        </div>
      </div>
    </div>
  );
};

const SsoTab: React.FC<{ orgId: string; ssoData: any }> = ({ orgId, ssoData }) => {
  const queryClient = useQueryClient();
  const [oidc, setOidc] = useState({ issuer: '', clientId: '', clientSecret: '', enabled: false });
  const [saml, setSaml] = useState({ entryPoint: '', issuer: '', cert: '', enabled: false });
  const [saved, setSaved] = useState(false);

  React.useEffect(() => {
    if (ssoData?.oidc) setOidc((p) => ({ ...p, ...ssoData.oidc, clientSecret: '' }));
    if (ssoData?.saml) setSaml((p) => ({ ...p, ...ssoData.saml, cert: '' }));
  }, [ssoData]);

  const apiBase = (import.meta as any).env?.VITE_API_BASE_URL || '';
  const acsUrl = `${apiBase}/sso/${orgId}/saml/acs`;
  const metadataUrl = `${apiBase}/sso/${orgId}/saml/metadata`;
  const oidcRedirect = `${apiBase}/sso/${orgId}/oidc/callback`;

  const save = useMutation({
    mutationFn: () => adminAPI.updateOrgSso(orgId, { oidc, saml }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-sso', orgId] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium mb-1">OpenID Connect (OIDC)</h3>
        <p className="text-xs text-gray-500 mb-3">Redirect URI to register with your IdP: <code className="break-all">{oidcRedirect}</code></p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center text-sm gap-2"><input type="checkbox" checked={oidc.enabled} onChange={(e) => setOidc({ ...oidc, enabled: e.target.checked })} /> Enabled</label>
          <div />
          <input placeholder="Issuer URL (e.g. https://accounts.google.com)" value={oidc.issuer} onChange={(e) => setOidc({ ...oidc, issuer: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          <input placeholder="Client ID" value={oidc.clientId} onChange={(e) => setOidc({ ...oidc, clientId: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          <input placeholder="Client Secret (blank = unchanged)" value={oidc.clientSecret} onChange={(e) => setOidc({ ...oidc, clientSecret: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium mb-1">SAML 2.0</h3>
        <p className="text-xs text-gray-500 mb-3">SP metadata: <code className="break-all">{metadataUrl}</code> · ACS URL: <code className="break-all">{acsUrl}</code></p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center text-sm gap-2"><input type="checkbox" checked={saml.enabled} onChange={(e) => setSaml({ ...saml, enabled: e.target.checked })} /> Enabled</label>
          <div />
          <input placeholder="IdP Entry Point (SSO URL)" value={saml.entryPoint} onChange={(e) => setSaml({ ...saml, entryPoint: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          <input placeholder="IdP Issuer / Entity ID" value={saml.issuer} onChange={(e) => setSaml({ ...saml, issuer: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          <textarea placeholder="IdP X.509 Certificate (blank = unchanged)" value={saml.cert} onChange={(e) => setSaml({ ...saml, cert: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm md:col-span-2 h-24" />
        </div>
      </div>

      <div className="flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-green-600">Saved</span>}
        <button onClick={() => save.mutate()} disabled={save.isLoading} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
          {save.isLoading ? 'Saving…' : 'Save SSO Configuration'}
        </button>
      </div>
    </div>
  );
};

const OrganizationRow: React.FC<{
  organization: Organization;
  onEdit: (org: Organization) => void;
  onDelete: (orgId: string) => void;
  onManageMembers: (org: Organization) => void;
  onViewDetails: (org: Organization) => void;
  isSelected: boolean;
  onSelect: (orgId: string) => void;
}> = ({ organization, onEdit, onDelete, onManageMembers, onViewDetails, isSelected, onSelect }) => {
  const [showMenu, setShowMenu] = useState(false);

  const statusColors = {
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-red-100 text-red-800',
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(organization.id)}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-blue-600" />
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {organization.name}
            </div>
            <div className="text-sm text-gray-500">
              {organization.domain || 'No domain'}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {organization.description || 'No description'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="flex items-center text-sm text-gray-900">
          <Users className="w-4 h-4 mr-2 text-gray-400" />
          {organization.memberCount || 0}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          statusColors[organization.status as keyof typeof statusColors]
        }`}>
          {organization.status}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div className="flex items-center">
          <Calendar className="w-3 h-3 mr-1" />
          {new Date(organization.createdAt || organization.created_at || Date.now()).toLocaleDateString()}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="text-gray-400 hover:text-gray-600"
          >
            <MoreVertical className="w-5 h-5" />
          </button>
          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
              <div className="py-1">
                <button
                  onClick={() => {
                    onViewDetails(organization);
                    setShowMenu(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  View Details
                </button>
                <button
                  onClick={() => {
                    onEdit(organization);
                    setShowMenu(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onManageMembers(organization);
                    setShowMenu(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Manage Members
                </button>
                <button
                  onClick={() => {
                    setShowMenu(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </button>
                <button
                  onClick={() => {
                    onDelete(organization.id);
                    setShowMenu(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

const OrganizationsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'createdAt' | 'memberCount'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | undefined>();
  
  // Bulk operations state
  const [selectedOrganizations, setSelectedOrganizations] = useState<Set<string>>(new Set());

  const queryClient = useQueryClient();

  const { data: organizationsData, isLoading } = useQuery({
    queryKey: ['organizations', searchTerm, statusFilter, sortBy, sortOrder, page],
    queryFn: () => adminAPI.getOrganizations({
      page,
      limit: 20,
      search: searchTerm || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      sort: sortBy,
      order: sortOrder,
    }),
  });

  const createOrgMutation = useMutation({
    mutationFn: (orgData: Partial<Organization>) => adminAPI.createOrganization(orgData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setShowModal(false);
      setSelectedOrganization(undefined);
    },
  });

  const updateOrgMutation = useMutation({
    mutationFn: ({ id, ...orgData }: Partial<Organization> & { id: string }) =>
      adminAPI.updateOrganization(id, orgData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setShowModal(false);
      setSelectedOrganization(undefined);
    },
  });

  const deleteOrgMutation = useMutation({
    mutationFn: (orgId: string) => adminAPI.deleteOrganization(orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });

  // Bulk operations mutations
  const bulkUpdateMutation = useMutation({
    mutationFn: ({ orgIds, updates }: { orgIds: string[], updates: Partial<Organization> }) =>
      Promise.all(orgIds.map(id => adminAPI.updateOrganization(id, updates))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setSelectedOrganizations(new Set());
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (orgIds: string[]) =>
      Promise.all(orgIds.map(id => adminAPI.deleteOrganization(id))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setSelectedOrganizations(new Set());
    },
  });

  const handleCreateOrganization = () => {
    setSelectedOrganization(undefined);
    setShowModal(true);
  };

  const handleEditOrganization = (organization: Organization) => {
    setSelectedOrganization(organization);
    setShowModal(true);
  };

  const handleManageMembers = (organization: Organization) => {
    setSelectedOrganization(organization);
    setShowMembersModal(true);
  };

  const handleViewDetails = (organization: Organization) => {
    setSelectedOrganization(organization);
    setShowDetailModal(true);
  };

  const handleSaveOrganization = (orgData: Partial<Organization>) => {
    if (selectedOrganization) {
      updateOrgMutation.mutate({ ...orgData, id: selectedOrganization.id });
    } else {
      createOrgMutation.mutate(orgData);
    }
  };

  const handleDeleteOrganization = (orgId: string) => {
    if (confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      deleteOrgMutation.mutate(orgId);
    }
  };

  // Bulk operations handlers
  const handleSelectAll = () => {
    if (selectedOrganizations.size === organizations.length) {
      setSelectedOrganizations(new Set());
    } else {
      setSelectedOrganizations(new Set(organizations.map(org => org.id)));
    }
  };

  const handleSelectOrganization = (orgId: string) => {
    const newSelected = new Set(selectedOrganizations);
    if (newSelected.has(orgId)) {
      newSelected.delete(orgId);
    } else {
      newSelected.add(orgId);
    }
    setSelectedOrganizations(newSelected);
  };

  const handleBulkStatusChange = (status: 'active' | 'suspended') => {
    if (selectedOrganizations.size > 0) {
      bulkUpdateMutation.mutate({
        orgIds: Array.from(selectedOrganizations),
        updates: { status }
      });
    }
  };

  const handleBulkDelete = () => {
    if (selectedOrganizations.size > 0 && 
        confirm(`Are you sure you want to delete ${selectedOrganizations.size} organizations? This action cannot be undone.`)) {
      bulkDeleteMutation.mutate(Array.from(selectedOrganizations));
    }
  };

  const organizations = organizationsData?.data || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
          <p className="text-gray-600">Manage organizations and their members</p>
        </div>
        <button
          onClick={handleCreateOrganization}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Organization
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="name">Name</option>
                <option value="createdAt">Created At</option>
                <option value="memberCount">Member Count</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as any)}
                className="w-24 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedOrganizations.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm font-medium text-blue-900">
                {selectedOrganizations.size} organization{selectedOrganizations.size > 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkStatusChange('active')}
                disabled={bulkUpdateMutation.isLoading}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                Activate
              </button>
              <button
                onClick={() => handleBulkStatusChange('suspended')}
                disabled={bulkUpdateMutation.isLoading}
                className="px-3 py-1 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 disabled:opacity-50"
              >
                Suspend
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleteMutation.isLoading}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedOrganizations(new Set())}
                className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organizations Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading organizations...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={organizations.length > 0 && selectedOrganizations.size === organizations.length}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Members
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {organizations.map((organization) => (
                  <OrganizationRow
                    key={organization.id}
                    organization={organization}
                    onEdit={handleEditOrganization}
                    onDelete={handleDeleteOrganization}
                    onManageMembers={handleManageMembers}
                    onViewDetails={handleViewDetails}
                    isSelected={selectedOrganizations.has(organization.id)}
                    onSelect={handleSelectOrganization}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && organizations.length === 0 && (
          <div className="p-8 text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No organizations found</p>
            <button
              onClick={handleCreateOrganization}
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              Create your first organization
            </button>
          </div>
        )}
      </div>

      {/* Pagination */}
      {organizationsData && organizationsData.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, organizationsData.pagination.total)} of {organizationsData.pagination.total} organizations
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === organizationsData.pagination.totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Organization Modal */}
      <OrganizationModal
        organization={selectedOrganization}
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedOrganization(undefined);
        }}
        onSave={handleSaveOrganization}
      />

      {/* Members Modal */}
      {selectedOrganization && (
        <MembersModal
          organization={selectedOrganization}
          isOpen={showMembersModal}
          onClose={() => {
            setShowMembersModal(false);
            setSelectedOrganization(undefined);
          }}
        />
      )}

      {/* Organization Detail Modal */}
      {selectedOrganization && (
        <OrganizationDetailModal
          organization={selectedOrganization}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedOrganization(undefined);
          }}
        />
      )}
    </div>
  );
};

export default OrganizationsPage;
